import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Heart, ArrowUp, ArrowDown, Minus, Clock, CalendarClock, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface RelationshipHealthSummaryProps {
  organizationId: string;
}

type HealthData = {
  score: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  recentTrend: 'increasing' | 'stable' | 'decreasing';
  lastActivity: string;
  nextScheduled: string;
  interactions: number;
  tasks: number;
};

export function RelationshipHealthSummary({ organizationId }: RelationshipHealthSummaryProps) {
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHealth = async () => {
      setLoading(true);
      try {
        // Get recent activities for this organization
        const { data: activities, error: activitiesError } = await supabase
          .from('activities')
          .select('*')
          .eq('organization_id', organizationId)
          .order('date', { ascending: false });

        if (activitiesError) throw activitiesError;

        // Get pending tasks for this organization
        const { data: tasks, error: tasksError } = await supabase
          .from('tasks')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('status', 'pending');

        if (tasksError) throw tasksError;

        // Calculate metrics
        const lastActivityDate =
          activities && activities.length > 0 ? new Date(activities[0].date) : null;

        const daysSinceLastActivity = lastActivityDate
          ? Math.floor((new Date().getTime() - lastActivityDate.getTime()) / (1000 * 3600 * 24))
          : null;

        const interactionCount = activities ? activities.length : 0;

        // Calculate health score based on recency and frequency
        let healthScore = 50; // Base score

        if (daysSinceLastActivity !== null) {
          if (daysSinceLastActivity < 7) {
            healthScore += 25;
          } else if (daysSinceLastActivity < 30) {
            healthScore += 10;
          } else if (daysSinceLastActivity > 90) {
            healthScore -= 20;
          }
        }

        if (interactionCount > 10) {
          healthScore += 15;
        } else if (interactionCount > 5) {
          healthScore += 10;
        } else if (interactionCount < 2) {
          healthScore -= 10;
        }

        // Cap score between 0-100
        healthScore = Math.max(0, Math.min(100, healthScore));

        // Determine sentiment and trend
        let sentiment: 'positive' | 'neutral' | 'negative' = 'neutral';
        if (healthScore >= 70) sentiment = 'positive';
        if (healthScore < 40) sentiment = 'negative';

        let recentTrend: 'increasing' | 'stable' | 'decreasing' = 'stable';
        if (daysSinceLastActivity !== null && daysSinceLastActivity < 14 && interactionCount > 3) {
          recentTrend = 'increasing';
        } else if (daysSinceLastActivity !== null && daysSinceLastActivity > 60) {
          recentTrend = 'decreasing';
        }

        const nextTask =
          tasks && tasks.length > 0
            ? tasks.sort(
                (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
              )[0]
            : null;

        setHealthData({
          score: healthScore,
          sentiment,
          recentTrend,
          lastActivity:
            daysSinceLastActivity !== null
              ? daysSinceLastActivity === 0
                ? 'Today'
                : `${daysSinceLastActivity} days ago`
              : 'None recorded',
          nextScheduled: nextTask
            ? new Date(nextTask.due_date) < new Date()
              ? `Overdue: ${nextTask.title}`
              : `${new Date(nextTask.due_date).toLocaleDateString()}: ${nextTask.title}`
            : 'None scheduled',
          interactions: interactionCount,
          tasks: tasks ? tasks.length : 0,
        });
      } catch (error) {
        // Error handling omitted intentionally
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchHealth();
    }
  }, [organizationId]);

  if (loading || !healthData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" /> Relationship Health
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <div className="animate-pulse">Loading relationship health...</div>
        </CardContent>
      </Card>
    );
  }

  const getTrendIcon = () => {
    switch (healthData.recentTrend) {
      case 'increasing':
        return <ArrowUp className="h-4 w-4 text-green-500" />;
      case 'decreasing':
        return <ArrowDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getScoreColor = () => {
    if (healthData.score >= 70) return 'text-green-500';
    if (healthData.score >= 40) return 'text-amber-500';
    return 'text-red-500';
  };

  const getProgressColor = () => {
    if (healthData.score >= 70) return 'bg-green-500';
    if (healthData.score >= 40) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5" /> Relationship Health
          </div>
          <div className="flex items-center gap-1">
            <span className={cn('text-lg font-bold', getScoreColor())}>{healthData.score}</span>
            {getTrendIcon()}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress value={healthData.score} className={cn('h-2', getProgressColor())} />

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" /> Last Activity
            </div>
            <div className="font-medium">{healthData.lastActivity}</div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarClock className="h-4 w-4" /> Next Task
            </div>
            <div className="font-medium">{healthData.nextScheduled}</div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" /> Interactions
            </div>
            <div className="font-medium">{healthData.interactions} total</div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarClock className="h-4 w-4" /> Open Tasks
            </div>
            <div className="font-medium">{healthData.tasks} tasks</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
